import React from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControlLabel,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import LaunchIcon from '@mui/icons-material/Launch';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../auth/AuthContext';
import {
  asPrettyJSON,
  canonicalRequest,
  EditorPageShell,
  EmptyState,
  parseJSONObject,
  RemoteEntityAutocomplete,
  SectionCard,
  useAddressableTabs,
} from './CanonicalEditorCommon';
import { EntityStatGrid, ReadinessPanel } from '../../components/workspace/EntityPanels';
import ResourceActivityPanel from '../../components/workspace/ResourceActivityPanel';
import { formatDate, labelize, StatusChip } from '../../components/workspace/WorkspaceStatus';
import config from '../../config';

const productStatuses = ['draft', 'ready', 'active', 'archived'];
const tabs = ['Overview', 'Variants', 'Placements', 'Mockups', 'Fulfillment', 'Listings', 'Activity'];

function temporaryKey(prefix) {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function emptyForm() {
  return {
    name: '',
    status: 'draft',
    design: null,
    template: null,
    offer: null,
    variants: [],
    placements: [],
    removedVariantIDs: [],
    removedPlacementIDs: [],
  };
}

function formFromProduct(product) {
  return {
    name: product.name || '',
    status: product.status || 'draft',
    design: product.design || null,
    template: product.catalog_template || null,
    offer: product.default_fulfillment_offer || null,
    variants: (product.variants || []).map((variant) => ({
      ...variant,
      _key: variant.id,
      optionsText: asPrettyJSON(variant.options),
    })),
    placements: (product.placements || []).map((placement) => ({
      ...placement,
      _key: placement.id,
      transformText: asPrettyJSON(placement.transform),
    })),
    removedVariantIDs: [],
    removedPlacementIDs: [],
  };
}

function productPayload(form, expectedVersion) {
  const payload = {
    name: form.name.trim(),
    status: form.status,
    design_id: form.design?.id || null,
    catalog_template_id: form.template?.id || null,
    default_fulfillment_offer_id: form.offer?.id || null,
    variants: [
      ...form.variants.map((variant, index) => ({
        ...(variant.id ? { id: variant.id } : {}),
        fulfillment_offer_variant_id: variant.fulfillment_offer_variant_id || variant.fulfillment_offer_variant?.id || null,
        sku: String(variant.sku || '').trim(),
        title: String(variant.title || '').trim(),
        options: parseJSONObject(variant.optionsText, `Variant ${index + 1} options`),
        enabled: variant.enabled !== false,
        sort_order: index,
      })),
      ...form.removedVariantIDs.map((id) => ({ id, remove: true })),
    ],
    placements: [
      ...form.placements.map((placement, index) => ({
        ...(placement.id ? { id: placement.id } : {}),
        product_variant_id: placement.product_variant_id || null,
        image_id: placement.image_id || null,
        placement_key: String(placement.placement_key || '').trim(),
        transform: parseJSONObject(placement.transformText, `Placement ${index + 1} transform`),
        sort_order: index,
      })),
      ...form.removedPlacementIDs.map((id) => ({ id, remove: true })),
    ],
  };
  if (expectedVersion) {
    payload.expected_version = expectedVersion;
  }
  return payload;
}

const CanonicalProductEditor = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { fetchWithAuth } = useAuth();
  const isNew = !id || id === 'new';
  const [product, setProduct] = React.useState(null);
  const [form, setForm] = React.useState(emptyForm);
  const [tab, setTab] = useAddressableTabs(tabs);
  const [loading, setLoading] = React.useState(!isNew);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState('');
  const [validation, setValidation] = React.useState(null);
  const [readiness, setReadiness] = React.useState(null);
  const [catalogDetail, setCatalogDetail] = React.useState(null);
  const [designDetail, setDesignDetail] = React.useState(null);
  const [mockupTemplateFile, setMockupTemplateFile] = React.useState(null);
  const [mockupBusy, setMockupBusy] = React.useState('');
  const [mockupNotice, setMockupNotice] = React.useState(null);
  const mockupTemplateInputRef = React.useRef(null);

  const loadProduct = React.useCallback(async () => {
    if (isNew) {
      setProduct(null);
      setForm(emptyForm());
      setLoading(false);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const payload = await canonicalRequest(fetchWithAuth, `/products/${id}`);
      setProduct(payload.item);
      setForm(formFromProduct(payload.item));
      setValidation(null);
      canonicalRequest(fetchWithAuth, `/products/${id}/validate`, { method: 'POST' })
        .then(setReadiness)
        .catch(() => setReadiness(null));
    } catch (loadError) {
      setError(loadError.message || 'Unable to load product');
    } finally {
      setLoading(false);
    }
  }, [fetchWithAuth, id, isNew]);

  React.useEffect(() => {
    loadProduct();
  }, [loadProduct]);

  React.useEffect(() => {
    if (!form.template?.id) {
      setCatalogDetail(null);
      return;
    }
    let active = true;
    canonicalRequest(fetchWithAuth, `/catalog/${form.template.id}`)
      .then((payload) => {
        if (!active) return;
        setCatalogDetail(payload.item);
        if (form.offer?.id) {
          const detailedOffer = (payload.item.offers || []).find((offer) => offer.id === form.offer.id);
          if (detailedOffer) {
            setForm((current) => ({ ...current, offer: detailedOffer }));
          }
        }
      })
      .catch((loadError) => active && setError(loadError.message || 'Unable to load catalog offer details'));
    return () => { active = false; };
  }, [fetchWithAuth, form.offer?.id, form.template?.id]);

  React.useEffect(() => {
    if (!form.design?.id) {
      setDesignDetail(null);
      return;
    }
    let active = true;
    canonicalRequest(fetchWithAuth, `/designs/${form.design.id}`)
      .then((payload) => active && setDesignDetail(payload.item))
      .catch((loadError) => active && setError(loadError.message || 'Unable to load design details'));
    return () => { active = false; };
  }, [fetchWithAuth, form.design?.id]);

  const setField = (field, value) => setForm((current) => ({ ...current, [field]: value }));

  const save = async () => {
    setSaving(true);
    setError('');
    setValidation(null);
    try {
      const payload = productPayload(form, product?.version);
      const result = await canonicalRequest(fetchWithAuth, isNew ? '/products' : `/products/${id}`, {
        method: isNew ? 'POST' : 'PATCH',
        body: JSON.stringify(payload),
      });
      setValidation(result.validation || null);
      setReadiness(result.validation || null);
      if (isNew) {
        navigate(`/products/${result.id}`, { replace: true });
      } else {
        await loadProduct();
      }
    } catch (saveError) {
      setValidation(saveError.validation || null);
      setError(saveError.status === 409
        ? 'This product changed since you opened it. Reload before applying your edits.'
        : saveError.message || 'Unable to save product');
    } finally {
      setSaving(false);
    }
  };

  const validate = async () => {
    if (isNew) {
      setError('Save the draft once before running a server readiness check.');
      return;
    }
    setError('');
    try {
      const result = await canonicalRequest(fetchWithAuth, `/products/${id}/validate`, { method: 'POST' });
      setValidation(result);
      setReadiness(result);
    } catch (validateError) {
      setValidation(validateError.validation || null);
      setError(validateError.message || 'Unable to validate product');
    }
  };

  const uploadMockupTemplate = async () => {
    if (isNew || !product?.id || !mockupTemplateFile) return;
    if (!mockupTemplateFile.name.toLowerCase().endsWith('.zip')) {
      setMockupNotice({ severity: 'error', text: 'Choose the ZIP package supplied with the mockup template.' });
      return;
    }
    if (mockupTemplateFile.size > 200 * 1024 * 1024) {
      setMockupNotice({ severity: 'error', text: 'The template package must be no larger than 200 MB.' });
      return;
    }
    setMockupBusy('upload');
    setMockupNotice(null);
    try {
      const body = new FormData();
      body.append('file', mockupTemplateFile, mockupTemplateFile.name);
      const response = await fetchWithAuth(`${config.apiBaseUrl}/products/${product.id}/mockup-template`, { method: 'POST', body });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.error || `Template upload failed with status ${response.status}`);
      setMockupTemplateFile(null);
      if (mockupTemplateInputRef.current) mockupTemplateInputRef.current.value = '';
      setMockupNotice({ severity: 'success', text: `${payload.item?.name || 'Featured mockup template'} uploaded and assigned.` });
      await loadProduct();
    } catch (uploadError) {
      setMockupNotice({ severity: 'error', text: uploadError.message || 'Unable to upload this template.' });
    } finally {
      setMockupBusy('');
    }
  };

  const generateFeaturedMockup = async (force = false) => {
    if (isNew || !product?.id) return;
    setMockupBusy('generate');
    setMockupNotice(null);
    try {
      const payload = await canonicalRequest(fetchWithAuth, `/products/${product.id}/featured-mockups`, {
        method: 'POST',
        body: JSON.stringify({ force }),
      });
      setMockupNotice({
        severity: 'info',
        text: `Featured mockup queued${payload.operation?.id ? ` as operation ${payload.operation.id}` : ''}. Progress is visible in Activity.`,
      });
      await loadProduct();
    } catch (generateError) {
      setMockupNotice({ severity: 'error', text: generateError.message || 'Unable to queue the featured mockup.' });
    } finally {
      setMockupBusy('');
    }
  };

  const approveFeaturedMockup = async (renderID) => {
    if (!renderID) return;
    setMockupBusy(`approve:${renderID}`);
    setMockupNotice(null);
    try {
      await canonicalRequest(fetchWithAuth, `/mockup-renders/${renderID}/approve`, { method: 'POST', body: '{}' });
      setMockupNotice({ severity: 'success', text: 'The reviewed image is now the featured mockup. The former primary image was retained in second position.' });
      await loadProduct();
    } catch (approveError) {
      setMockupNotice({ severity: 'error', text: approveError.message || 'Unable to approve this mockup.' });
    } finally {
      setMockupBusy('');
    }
  };

  const addVariant = () => {
    setForm((current) => ({
      ...current,
      variants: [...current.variants, {
        _key: temporaryKey('variant'), fulfillment_offer_variant_id: null, sku: '', title: '', optionsText: '{}', enabled: true,
      }],
    }));
  };

  const useOfferVariants = () => {
    const variants = form.offer?.variants || [];
    if (!variants.length) {
      setError('This fulfillment offer does not have normalized variants yet.');
      return;
    }
    setForm((current) => ({
      ...current,
      variants: variants.map((variant) => ({
        _key: temporaryKey('variant'),
        fulfillment_offer_variant_id: variant.id,
        fulfillment_offer_variant: variant,
        sku: '',
        title: variant.title || variant.external_id,
        optionsText: asPrettyJSON(variant.options),
        enabled: variant.available !== false,
      })),
      removedVariantIDs: [...current.removedVariantIDs, ...current.variants.filter((item) => item.id).map((item) => item.id)],
    }));
  };

  const updateVariant = (index, patch) => {
    setForm((current) => ({
      ...current,
      variants: current.variants.map((variant, itemIndex) => itemIndex === index ? { ...variant, ...patch } : variant),
    }));
  };

  const removeVariant = (index) => {
    setForm((current) => {
      const target = current.variants[index];
      return {
        ...current,
        variants: current.variants.filter((item, itemIndex) => itemIndex !== index),
        removedVariantIDs: target?.id ? [...current.removedVariantIDs, target.id] : current.removedVariantIDs,
      };
    });
  };

  const addPlacement = (placementKey = '') => {
    setForm((current) => ({
      ...current,
      placements: [...current.placements, {
        _key: temporaryKey('placement'),
        product_variant_id: null,
        image_id: designDetail?.image_id || null,
        placement_key: placementKey,
        transformText: '{}',
      }],
    }));
  };

  const useOfferPlacements = () => {
    const placements = form.offer?.placements || [];
    if (!placements.length) {
      setError('This fulfillment offer does not have normalized print placements yet.');
      return;
    }
    setForm((current) => ({
      ...current,
      placements: placements.map((placement) => ({
        _key: temporaryKey('placement'),
        product_variant_id: null,
        image_id: designDetail?.image_id || null,
        placement_key: placement.placement_key,
        transformText: asPrettyJSON(placement.default_transform),
      })),
      removedPlacementIDs: [...current.removedPlacementIDs, ...current.placements.filter((item) => item.id).map((item) => item.id)],
    }));
  };

  const updatePlacement = (index, patch) => {
    setForm((current) => ({
      ...current,
      placements: current.placements.map((placement, itemIndex) => itemIndex === index ? { ...placement, ...patch } : placement),
    }));
  };

  const removePlacement = (index) => {
    setForm((current) => {
      const target = current.placements[index];
      return {
        ...current,
        placements: current.placements.filter((item, itemIndex) => itemIndex !== index),
        removedPlacementIDs: target?.id ? [...current.removedPlacementIDs, target.id] : current.removedPlacementIDs,
      };
    });
  };

  const readinessChecks = [
    { key: 'design', label: 'Design selected', complete: Boolean(form.design?.id), help: 'Reusable artwork is attached.' },
    { key: 'catalog', label: 'Catalog product selected', complete: Boolean(form.template?.id), help: 'The normalized product type is known.' },
    { key: 'offer', label: 'Fulfillment offer selected', complete: Boolean(form.offer?.id), help: 'A provider can manufacture the product.' },
    { key: 'variants', label: 'Enabled variants configured', complete: form.variants.some((variant) => variant.enabled !== false), help: `${form.variants.filter((variant) => variant.enabled !== false).length} enabled.` },
    { key: 'placements', label: 'Artwork placement configured', complete: form.placements.length > 0, help: `${form.placements.length} placement${form.placements.length === 1 ? '' : 's'}.` },
    { key: 'listing', label: 'Listing draft created', complete: (product?.listings || []).length > 0, help: 'Merchandising content can be published independently.' },
  ];

  return (
    <EditorPageShell
      backTo="/products"
      eyebrow={isNew ? 'Create product' : 'Product'}
      title={form.name || (isNew ? 'Untitled product' : 'Product')}
      subtitle={isNew ? 'Prepare a provider-neutral product without changing any external account.' : product?.id}
      status={form.status}
      version={product?.version}
      loading={loading}
      saving={saving}
      error={error}
      validation={validation}
      onReload={isNew ? null : loadProduct}
      onValidate={validate}
      onSave={save}
      extraActions={!isNew ? (
        <>
          <Button variant="outlined" onClick={() => navigate(`/launch?product_id=${id}`)}>Continue launch</Button>
          <Button variant="outlined" startIcon={<LaunchIcon />} onClick={() => navigate(`/listings/new?product_id=${id}`)}>
            Create listing
          </Button>
        </>
      ) : null}
    >
      <Paper variant="outlined">
        <Tabs value={tab} onChange={(event, next) => setTab(next)} variant="scrollable" scrollButtons="auto" aria-label="Product editor sections">
          {tabs.map((label, index) => <Tab key={label} id={`product-editor-tab-${index}`} aria-controls={`product-editor-panel-${index}`} label={label} />)}
        </Tabs>
      </Paper>

      <Box role="tabpanel" id={`product-editor-panel-${tab}`} aria-labelledby={`product-editor-tab-${tab}`} tabIndex={0}>
      {tab === 0 ? (
        <Stack spacing={2}>
          {!isNew ? (
            <>
              <EntityStatGrid items={[
                { label: 'Variants', value: form.variants.length, helper: `${form.variants.filter((variant) => variant.enabled !== false).length} enabled` },
                { label: 'Placements', value: form.placements.length, helper: form.placements.map((item) => item.placement_key).filter(Boolean).join(', ') || 'None yet' },
                { label: 'Fulfillment instances', value: (product?.fulfillment_instances || []).length, helper: 'Provider materializations' },
                { label: 'Listings', value: (product?.listings || []).length, helper: 'Reusable sales content' },
              ]} />
              <ReadinessPanel
                validation={readiness}
                checks={readinessChecks}
                title="Product readiness"
                description="Complete the reusable product intent before creating or reconciling provider copies."
              />
            </>
          ) : null}
          <SectionCard title="Product identity" description="This is the stable PODBoss product. Provider copies will be fulfillment instances of it.">
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '2fr 1fr' }, gap: 2 }}>
              <TextField label="Product name" required value={form.name} onChange={(event) => setField('name', event.target.value)} />
              <TextField select label="Lifecycle status" value={form.status} onChange={(event) => setField('status', event.target.value)}>
                {productStatuses.map((status) => <MenuItem key={status} value={status}>{status}</MenuItem>)}
              </TextField>
            </Box>
          </SectionCard>
          <SectionCard title="Design and catalog" description="Choose the artwork, normalized product type, and preferred way to manufacture it.">
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' }, gap: 2 }}>
              <RemoteEntityAutocomplete
                fetchWithAuth={fetchWithAuth}
                endpoint="/designs"
                label="Design"
                value={form.design}
                onChange={(value) => setField('design', value)}
                getOptionLabel={(option) => option.filename}
                helperText="Search reusable artwork by filename."
              />
              <RemoteEntityAutocomplete
                fetchWithAuth={fetchWithAuth}
                endpoint="/catalog"
                label="Catalog product type"
                value={form.template}
                onChange={(value) => setForm((current) => ({ ...current, template: value, offer: null }))}
                getOptionLabel={(option) => option.name}
                helperText="Search provider-neutral catalog templates."
              />
              <TextField
                select
                label="Default fulfillment offer"
                value={form.offer?.id || ''}
                onChange={(event) => setField('offer', (catalogDetail?.offers || []).find((offer) => offer.id === event.target.value) || null)}
                disabled={!form.template || !catalogDetail}
                helperText="A provider-specific blueprint and print-provider combination."
              >
                <MenuItem value=""><em>Not selected</em></MenuItem>
                {(catalogDetail?.offers || []).map((offer) => (
                  <MenuItem key={offer.id} value={offer.id}>{offer.title} · {offer.connection?.display_name || offer.provider}</MenuItem>
                ))}
              </TextField>
              <Box sx={{ minHeight: 56, display: 'flex', alignItems: 'center', gap: 2 }}>
                {form.design?.image_url ? (
                  <Box component="img" src={form.design.image_url} alt={form.design.filename || 'Selected design'} sx={{ width: 64, height: 64, objectFit: 'contain', borderRadius: 1, bgcolor: 'background.default' }} />
                ) : null}
                <Typography variant="body2" color="text.secondary">
                  {form.offer ? `${form.offer.provider} · ${(form.offer.variants || []).length} available variants` : 'You can save an incomplete draft and finish it later.'}
                </Typography>
              </Box>
            </Box>
          </SectionCard>
        </Stack>
      ) : null}

      {tab === 1 ? (
        <SectionCard
          title="Product variants"
          description="SKUs are stable PODBoss identifiers. Leave a new SKU blank and PODBoss will generate one when saved."
          action={(
            <Stack direction="row" spacing={1}>
              <Button size="small" variant="outlined" onClick={useOfferVariants} disabled={!form.offer}>Use offer variants</Button>
              <Button size="small" variant="contained" startIcon={<AddIcon />} onClick={addVariant}>Add variant</Button>
            </Stack>
          )}
        >
          {form.variants.length === 0 ? (
            <EmptyState title="No variants yet" description="Import the selected offer's variants or add a custom variant." />
          ) : (
            <Stack spacing={1.5}>
              {form.variants.map((variant, index) => (
                <Paper key={variant._key || variant.id} variant="outlined" sx={{ p: 2 }}>
                  <Stack spacing={1.5}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Typography variant="subtitle2">Variant {index + 1}</Typography>
                      <Tooltip title="Remove variant"><IconButton size="small" onClick={() => removeVariant(index)} aria-label={`Remove variant ${index + 1}`}><DeleteOutlineIcon /></IconButton></Tooltip>
                    </Stack>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1.3fr 1.3fr auto' }, gap: 1.5, alignItems: 'start' }}>
                      <TextField label="SKU" value={variant.sku || ''} onChange={(event) => updateVariant(index, { sku: event.target.value })} helperText={variant.id ? 'Stable identifier' : 'Blank generates a stable SKU'} />
                      <TextField label="Variant title" value={variant.title || ''} onChange={(event) => updateVariant(index, { title: event.target.value })} />
                      <TextField
                        select
                        label="Fulfillment variant"
                        value={variant.fulfillment_offer_variant_id || ''}
                        onChange={(event) => {
                          const offerVariant = (form.offer?.variants || []).find((item) => item.id === event.target.value) || null;
                          updateVariant(index, { fulfillment_offer_variant_id: offerVariant?.id || null, fulfillment_offer_variant: offerVariant });
                        }}
                        disabled={!form.offer}
                        helperText={variant.fulfillment_offer_variant ? `${variant.fulfillment_offer_variant.currency} ${(Number(variant.fulfillment_offer_variant.cost || 0) / 100).toFixed(2)} cost` : 'Required for exact cost pricing'}
                      >
                        <MenuItem value=""><em>Not mapped</em></MenuItem>
                        {(form.offer?.variants || []).map((item) => (
                          <MenuItem key={item.id} value={item.id}>{item.title || item.external_id} · {item.currency} {(Number(item.cost || 0) / 100).toFixed(2)}</MenuItem>
                        ))}
                      </TextField>
                      <FormControlLabel control={<Checkbox checked={variant.enabled !== false} onChange={(event) => updateVariant(index, { enabled: event.target.checked })} />} label="Enabled" />
                    </Box>
                    <TextField label="Normalized options (JSON)" multiline minRows={2} value={variant.optionsText || '{}'} onChange={(event) => updateVariant(index, { optionsText: event.target.value })} />
                  </Stack>
                </Paper>
              ))}
            </Stack>
          )}
        </SectionCard>
      ) : null}

      {tab === 2 ? (
        <SectionCard
          title="Artwork placements"
          description="Placements describe user intent. A fulfillment adapter translates these into provider print areas."
          action={(
            <Stack direction="row" spacing={1}>
              <Button size="small" variant="outlined" onClick={useOfferPlacements} disabled={!form.offer}>Use offer placements</Button>
              <Button size="small" variant="contained" startIcon={<AddIcon />} onClick={() => addPlacement()}>Add placement</Button>
            </Stack>
          )}
        >
          {!designDetail?.image_id ? <Alert severity="info">Choose a design to automatically use its image in new placements.</Alert> : null}
          {form.placements.length === 0 ? (
            <EmptyState title="No placements yet" description="Add a front, back, sleeve, or other supported placement." />
          ) : (
            <Stack spacing={1.5}>
              {form.placements.map((placement, index) => (
                <Paper key={placement._key || placement.id} variant="outlined" sx={{ p: 2 }}>
                  <Stack spacing={1.5}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Typography variant="subtitle2">Placement {index + 1}</Typography>
                      <Tooltip title="Remove placement"><IconButton size="small" onClick={() => removePlacement(index)} aria-label={`Remove placement ${index + 1}`}><DeleteOutlineIcon /></IconButton></Tooltip>
                    </Stack>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1.4fr' }, gap: 1.5 }}>
                      <TextField label="Placement key" required value={placement.placement_key || ''} onChange={(event) => updatePlacement(index, { placement_key: event.target.value })} placeholder="front" />
                      <TextField select label="Variant scope" value={placement.product_variant_id || ''} onChange={(event) => updatePlacement(index, { product_variant_id: event.target.value || null })}>
                        <MenuItem value="">All variants</MenuItem>
                        {form.variants.filter((variant) => variant.id).map((variant) => <MenuItem key={variant.id} value={variant.id}>{variant.title || variant.sku}</MenuItem>)}
                      </TextField>
                      <TextField label="Image UUID" required value={placement.image_id || ''} onChange={(event) => updatePlacement(index, { image_id: event.target.value || null })} />
                    </Box>
                    <TextField label="Placement transform (JSON)" multiline minRows={2} value={placement.transformText || '{}'} onChange={(event) => updatePlacement(index, { transformText: event.target.value })} />
                  </Stack>
                </Paper>
              ))}
            </Stack>
          )}
        </SectionCard>
      ) : null}

      {tab === 3 ? (
        <Stack spacing={2}>
          <SectionCard
            title="Featured mockup coverage"
            description="A primary provider image is useful catalog media, but only an approved custom render is counted as a featured mockup."
            action={!isNew ? (
              <Button
                variant="contained"
                startIcon={mockupBusy === 'generate' ? <CircularProgress size={16} color="inherit" /> : <AutoAwesomeOutlinedIcon />}
                disabled={
                  !product?.mockup_coverage?.has_template
                  || ['rendering', 'needs_approval'].includes(product?.mockup_coverage?.status)
                  || mockupBusy !== ''
                }
                onClick={() => generateFeaturedMockup(
                  product?.mockup_coverage?.status === 'ready'
                  || product?.mockup_coverage?.latest_render_status === 'rejected',
                )}
              >
                {product?.mockup_coverage?.status === 'ready'
                  ? 'Regenerate featured mockup'
                  : product?.mockup_coverage?.latest_render_status === 'rejected'
                    ? 'Generate revised mockup'
                  : product?.mockup_coverage?.status === 'failed'
                    ? 'Retry featured mockup'
                    : 'Generate featured mockup'}
              </Button>
            ) : null}
          >
            {mockupNotice ? <Alert severity={mockupNotice.severity} role="status">{mockupNotice.text}</Alert> : null}
            {isNew ? (
              <EmptyState title="Save this product first" description="A saved product can inherit a reusable template and create durable featured mockups." />
            ) : (
              <Stack spacing={2}>
                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                  <StatusChip status={product?.mockup_coverage?.status || 'missing_template'} />
                  <Chip size="small" variant="outlined" label={product?.mockup_coverage?.has_primary_media ? 'Primary media available' : 'No primary media'} />
                  <Chip size="small" variant="outlined" label={product?.mockup_coverage?.has_template ? `Template: ${product.mockup_coverage.template_name || 'Assigned'}` : 'No template assigned'} />
                  <Chip size="small" variant="outlined" label={`${product?.mockup_coverage?.listing_count || 0} listing${product?.mockup_coverage?.listing_count === 1 ? '' : 's'}`} />
                </Stack>

                {!product?.mockup_coverage?.has_template ? (
                  <Alert severity="warning">
                    This product has no reusable featured template. Upload Jorian&apos;s package below; it will be assigned to this fulfillment offer so sibling products can use it too.
                  </Alert>
                ) : null}
                {product?.mockup_coverage?.latest_render_error ? (
                  <Alert severity="error">{product.mockup_coverage.latest_render_error}</Alert>
                ) : null}

                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' }, gap: 2 }}>
                  <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
                    <Box sx={{ aspectRatio: '1 / 1', display: 'grid', placeItems: 'center', bgcolor: 'surfaceContainerLow' }}>
                      {product?.mockup_coverage?.featured_mockup_url ? (
                        <Box component="img" src={product.mockup_coverage.featured_mockup_url} alt="Approved featured mockup" sx={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                      ) : (
                        <Typography variant="body2" color="text.secondary">No approved featured mockup</Typography>
                      )}
                    </Box>
                    <Box sx={{ p: 1.5 }}>
                      <Typography variant="subtitle2">Featured</Typography>
                      <Typography variant="caption" color="text.secondary">Purpose-built hero image used ahead of provider views.</Typography>
                    </Box>
                  </Paper>
                  <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
                    <Box sx={{ aspectRatio: '1 / 1', display: 'grid', placeItems: 'center', bgcolor: 'surfaceContainerLow' }}>
                      {product?.mockup_coverage?.primary_media_url ? (
                        <Box component="img" src={product.mockup_coverage.primary_media_url} alt="Current primary listing image" sx={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                      ) : (
                        <Typography variant="body2" color="text.secondary">No listing image</Typography>
                      )}
                    </Box>
                    <Box sx={{ p: 1.5 }}>
                      <Typography variant="subtitle2">Current primary media</Typography>
                      <Typography variant="caption" color="text.secondary">Shown separately; this may be a standard Printify mockup.</Typography>
                    </Box>
                  </Paper>
                </Box>
              </Stack>
            )}
          </SectionCard>

          {!isNew ? (
            <SectionCard title="Upload a reusable template" description="Upload a versioned ZIP containing template.json and Jorian's layered PNG/JPEG assets. No visual inference is used during rendering.">
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }}>
                <Button component="label" variant="outlined" startIcon={<CloudUploadOutlinedIcon />} disabled={mockupBusy !== ''}>
                  Choose template ZIP
                  <input ref={mockupTemplateInputRef} hidden type="file" accept=".zip,application/zip" onChange={(event) => setMockupTemplateFile(event.target.files?.[0] || null)} />
                </Button>
                <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
                  {mockupTemplateFile ? `${mockupTemplateFile.name} · ${(mockupTemplateFile.size / 1024 / 1024).toFixed(1)} MB` : 'No package selected'}
                </Typography>
                <Button
                  variant="contained"
                  onClick={uploadMockupTemplate}
                  disabled={!mockupTemplateFile || mockupBusy !== ''}
                  startIcon={mockupBusy === 'upload' ? <CircularProgress size={16} color="inherit" /> : <CloudUploadOutlinedIcon />}
                >
                  Upload and assign
                </Button>
              </Stack>
              <Typography variant="caption" color="text.secondary">
                New versions are immutable. To revise a template, increment its version in template.json and upload a new package.
              </Typography>
            </SectionCard>
          ) : null}

          {!isNew ? (
            <SectionCard title="Recent renders" description="Generated and adopted hero images remain auditable, including failures and manual approvals.">
              {(product?.mockup_renders || []).length === 0 ? (
                <EmptyState title="No featured render history" description="Existing primary images remain unchanged until a reviewed image is adopted or a template render succeeds." />
              ) : (
                <Stack spacing={1.25}>
                  {product.mockup_renders.map((render) => (
                    <Paper key={render.id} variant="outlined" sx={{ p: 1.5 }}>
                      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }}>
                        {render.output_url ? <Box component="img" src={render.output_url} alt="" sx={{ width: 72, height: 72, objectFit: 'contain', borderRadius: 1, bgcolor: 'surfaceContainerLow' }} /> : null}
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                          <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" alignItems="center">
                            <StatusChip status={render.approved_at ? 'approved' : render.status} />
                            <Chip size="small" variant="outlined" label={labelize(render.renderer)} />
                            {render.template_name ? <Chip size="small" variant="outlined" label={render.template_name} /> : null}
                          </Stack>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
                            {formatDate(render.completed_at || render.created_at)} · {render.attempts || 0} attempt{render.attempts === 1 ? '' : 's'}
                          </Typography>
                          {render.last_error ? <Typography variant="body2" color="error.main" sx={{ mt: 0.5 }}>{render.last_error}</Typography> : null}
                        </Box>
                        {render.status === 'succeeded' && !render.approved_at ? (
                          <Button
                            variant="contained"
                            startIcon={mockupBusy === `approve:${render.id}` ? <CircularProgress size={16} color="inherit" /> : <CheckCircleOutlineIcon />}
                            disabled={mockupBusy !== ''}
                            onClick={() => approveFeaturedMockup(render.id)}
                          >
                            Approve
                          </Button>
                        ) : null}
                      </Stack>
                    </Paper>
                  ))}
                </Stack>
              )}
            </SectionCard>
          ) : null}
        </Stack>
      ) : null}

      {tab === 4 ? (
        <SectionCard title="Fulfillment instances" description="Provider materializations remain read-only until a publication workflow explicitly creates or updates them.">
          {(product?.fulfillment_instances || []).length === 0 ? (
            <EmptyState title="Not materialized yet" description="A publication route can create this product in the required fulfillment shop later." />
          ) : (
            <Stack spacing={1}>
              {product.fulfillment_instances.map((instance) => (
                <Paper key={instance.id} variant="outlined" sx={{ p: 2 }}>
                  <Stack spacing={1.5}>
                    <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1} alignItems={{ sm: 'center' }}>
                      <Box>
                        <Typography variant="subtitle2">{instance.connection?.display_name || labelize(instance.provider)}</Typography>
                        <Typography variant="body2" color="text.secondary">{labelize(instance.provider)} · {instance.external_id || 'Awaiting provider ID'}</Typography>
                      </Box>
                      <StatusChip status={instance.status} />
                    </Stack>
                    <EntityStatGrid items={[
                      { label: 'Mapped variants', value: (instance.variants || []).length },
                      { label: 'Observed', value: formatDate(instance.observed_at) },
                      { label: 'Source instance', value: instance.source_instance_id ? 'Cloned' : 'Original' },
                    ]} />
                    {(instance.variants || []).length ? (
                      <Box>
                        <Typography variant="caption" color="text.secondary">Provider variant mappings</Typography>
                        <Typography variant="body2">{instance.variants.slice(0, 8).map((variant) => variant.sku || variant.external_id).join(', ')}{instance.variants.length > 8 ? ` +${instance.variants.length - 8} more` : ''}</Typography>
                      </Box>
                    ) : null}
                  </Stack>
                </Paper>
              ))}
            </Stack>
          )}
        </SectionCard>
      ) : null}

      {tab === 5 ? (
        <SectionCard title="Listings" description="A Product can have reusable merchandising drafts and independent channel publications.">
          {(product?.listings || []).length === 0 ? (
            <EmptyState title="No listings yet" description="Create a reusable listing draft without publishing it anywhere." action={!isNew ? <Button variant="contained" onClick={() => navigate(`/listings/new?product_id=${id}`)}>Create listing</Button> : null} />
          ) : (
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'repeat(2, minmax(0, 1fr))' }, gap: 1.5 }}>
              {product.listings.map((listing) => (
                <Paper key={listing.id} variant="outlined" sx={{ p: 2 }}>
                  <Stack spacing={1.5} sx={{ height: '100%' }}>
                    <Stack direction="row" justifyContent="space-between" spacing={1} alignItems="flex-start">
                      <Typography variant="subtitle2">{listing.title || 'Untitled listing'}</Typography>
                      <StatusChip status={listing.status} />
                    </Stack>
                    <Button onClick={() => navigate(`/listings/${listing.id}`)} sx={{ mt: 'auto', alignSelf: 'flex-start' }}>Open listing</Button>
                  </Stack>
                </Paper>
              ))}
            </Box>
          )}
        </SectionCard>
      ) : null}

      {tab === 6 ? (
        <ResourceActivityPanel
          resources={isNew ? [] : [{ type: 'product', id }]}
          title="Product activity"
          description="Durable workflows and provider operations scoped to this product."
        />
      ) : null}
      </Box>
      {tabs.map((label, index) => index === tab ? null : (
        <Box key={label} role="tabpanel" id={`product-editor-panel-${index}`} aria-labelledby={`product-editor-tab-${index}`} hidden />
      ))}
    </EditorPageShell>
  );
};

export default CanonicalProductEditor;
